/**
 * The production `ChatHub` (`model/chatSession`): the one SignalR connection to `/hubs/chat`, for
 * the whole signed-in app.
 *
 * ── One socket, many listeners ───────────────────────────────────────────────────────────────
 * The navigation's unread dot, the inbox, an open thread and the Zadania panel all listen to the
 * same three events. A connection per surface would join the trainer to every conversation group
 * once per surface and deliver each message several times, so the socket is module state, started
 * by the shell's chat session (`ChatSessionProvider`) and read everywhere else through it.
 *
 * ── What the server says, and what is asked of it ────────────────────────────────────────────
 * `ReceiveMessage(message)` for every message in a conversation the trainer is in — including the
 * second broadcast when a film or a voice note finishes processing. `ConversationCreated(conv)`
 * when someone opens a new one; the socket is not in its group yet, so it is joined at once.
 * `UserTyping(conversationId, userId)`. The client invokes `JoinConversation` and `SendTyping`.
 * The backend's own contract: `ChatRealtimeService.cs`, keep-alive 7.5 s, server timeout 15 s.
 *
 * ── Reconnecting ─────────────────────────────────────────────────────────────────────────────
 * SignalR's automatic reconnect covers a dropped socket for its schedule and then gives up; a
 * first connect that fails is not covered at all. Both end in `onclose`, which starts again on a
 * capped backoff for as long as the shell holds the connection. The token is read fresh for every
 * attempt, because the server drops the socket when the token it was opened with expires.
 *
 * `status` is what the screens draw: `connecting` on the first attempt only (drawn as connected,
 * so a page load does not flash a warning), `reconnecting` once a live socket was lost or the first
 * attempt failed. After a reconnect, `resynced` is reported, because whatever was said while the
 * socket was down was never delivered — what to do about it is the session's.
 */

import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
  type HubConnection,
} from '@microsoft/signalr';

import { realtimeTransport } from '~/shared/api/mutator';
import type {
  TrainWithMeModulesChatDtosResponsesConversationResponse as ConversationResponse,
  TrainWithMeModulesChatDtosResponsesMessageResponse as MessageResponse,
} from '~/shared/api/generated/model';

import type { ChatHub, HubEvents, RealtimeStatus } from '../model/chatSession';

const RECONNECT_DELAYS_MS = [0, 2_000, 5_000, 10_000, 30_000];
const RESTART_CAP_MS = 30_000;

/** The production `ChatHub`: one SignalR connection to `/hubs/chat`, opened by `connect`. */
export function signalRHub(): ChatHub {
  let connection: HubConnection | null = null;
  let wanted = false;
  let restartTimer: ReturnType<typeof setTimeout> | undefined;
  let restartAttempt = 0;
  let everConnected = false;
  let status: RealtimeStatus = 'idle';
  let events: HubEvents | null = null;

  const setStatus = (next: RealtimeStatus) => {
    if (next === status) return;
    status = next;
    events?.status(next);
  };

  const build = (): HubConnection | null => {
    const transport = realtimeTransport();
    if (transport === null) return null;
    const hub = new HubConnectionBuilder()
      .withUrl(`${transport.baseUrl}/hubs/chat`, {
        accessTokenFactory: async () => (await transport.accessToken()) ?? '',
      })
      .withAutomaticReconnect(RECONNECT_DELAYS_MS)
      .withServerTimeout(15_000)
      .withKeepAliveInterval(7_500)
      .configureLogging(LogLevel.None)
      .build();
    hub.on('ReceiveMessage', (message: MessageResponse) => {
      events?.message(message);
    });
    hub.on('ConversationCreated', (conversation: ConversationResponse) => {
      events?.created(conversation);
    });
    hub.on('UserTyping', (conversationId: number, userId: string) => {
      events?.typing(conversationId, userId);
    });
    hub.onreconnecting(() => {
      setStatus('reconnecting');
    });
    hub.onreconnected(() => {
      setStatus('connected');
      events?.resynced();
    });
    hub.onclose(() => {
      if (!wanted) return;
      setStatus('reconnecting');
      scheduleRestart();
    });
    return hub;
  };

  const scheduleRestart = () => {
    clearTimeout(restartTimer);
    const delay = Math.min(RESTART_CAP_MS, 1_000 * 2 ** restartAttempt);
    restartAttempt += 1;
    restartTimer = setTimeout(() => {
      void begin();
    }, delay);
  };

  const begin = async () => {
    if (!wanted) return;
    if (connection === null) connection = build();
    const hub = connection;
    if (hub === null) {
      scheduleRestart();
      return;
    }
    if (hub.state !== HubConnectionState.Disconnected) return;
    if (!everConnected) setStatus('connecting');
    try {
      await hub.start();
      if (!wanted) {
        await hub.stop();
        return;
      }
      restartAttempt = 0;
      const wasConnected = everConnected;
      everConnected = true;
      setStatus('connected');
      if (wasConnected) events?.resynced();
    } catch {
      if (!wanted) return;
      setStatus('reconnecting');
      scheduleRestart();
    }
  };

  const invoke = (method: string, conversationId: number) => {
    const hub = connection;
    if (hub?.state !== HubConnectionState.Connected) return;
    // Best effort: the next connect joins every conversation anyway, and a frozen thread refuses typing.
    hub.invoke(method, conversationId).catch(() => undefined);
  };

  return {
    connect(next) {
      events = next;
      wanted = true;
      restartAttempt = 0;
      void begin();
      return () => {
        wanted = false;
        clearTimeout(restartTimer);
        const hub = connection;
        connection = null;
        everConnected = false;
        setStatus('idle');
        events = null;
        if (hub !== null) void hub.stop().catch(() => undefined);
      };
    },
    join(conversationId) {
      invoke('JoinConversation', conversationId);
    },
    typing(conversationId) {
      invoke('SendTyping', conversationId);
    },
  };
}
